import { useAuthStore } from "@/features/auth/auth.store";
import {
	paymentApi,
	registerBillingKeyForPlan,
} from "@/features/payment/payment.api";
import { rootRoute } from "@/routes";
import { Footer, NavBar, PricingCard } from "@/shared/ui/composites";
import { HStack, VStack } from "@/shared/ui/primitives";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

const CONTACT_EMAIL = "inflectco@gmail.com";

const plans = [
	{
		key: "BASIC",
		titleKey: "pricing.basicTitle",
		descKey: "pricing.basicDescription",
		price: 50_000,
		llm: "Gemini 2.5 Flash",
		maxWorkflows: 1,
		monthlyGyul: 50,
		dashboardKey: "pricing.dashboardNotProvided",
		enterpriseKey: "pricing.enterpriseNotAvailable",
	},
	{
		key: "PRO",
		titleKey: "pricing.proTitle",
		descKey: "pricing.proDescription",
		price: 150_000,
		llm: "Gemini 2.5 Pro",
		maxWorkflows: 3,
		monthlyGyul: 200,
		dashboardKey: "pricing.dashboardProvided",
		enterpriseKey: "pricing.enterpriseNotAvailable",
	},
	{
		key: "ENTERPRISE",
		titleKey: "pricing.enterpriseTitle",
		descKey: "pricing.enterpriseDescription",
		price: 500_000,
		llm: "Gemini 3 Pro",
		maxWorkflows: 10,
		monthlyGyul: 300,
		dashboardKey: "pricing.dashboardProvided",
		enterpriseKey: "pricing.enterpriseAvailable",
	},
] as const;

const TOSS_CLIENT_KEY =
	import.meta.env.VITE_TOSS_CLIENT_KEY || "test_ck_0RnYX2w532DZKROLneEPrNeyqApQ";

const Desktop = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const accessToken = useAuthStore((s) => s.accessToken);
	const user = useAuthStore((s) => s.user);
	const isLoggedIn = !!accessToken;
	const [planMessage, setPlanMessage] = useState<string | null>(null);
	const [planLoading, setPlanLoading] = useState(false);
	const processedAuthRef = useRef(false);

	// Handle redirect after Toss billing auth for plan subscription
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const billing = params.get("billing");
		const authKey = params.get("authKey");
		const customerKey = params.get("customerKey");
		const pendingPlan = params.get("plan");
		if (billing === "fail") {
			setPlanMessage(params.get("message") || t("pricing.billingRegisterFailed"));
			window.history.replaceState({}, "", "/pricing");
			return;
		}
		if (!isLoggedIn || !authKey || !customerKey || !pendingPlan || processedAuthRef.current) return;
		processedAuthRef.current = true;
		let cancelled = false;
		(async () => {
			setPlanLoading(true);
			setPlanMessage(null);
			const regResult = await registerBillingKeyForPlan(authKey, customerKey);
			if (cancelled) return;
			if (!regResult.success) {
				setPlanLoading(false);
				setPlanMessage(regResult.error || t("pricing.billingKeyFailed"));
				window.history.replaceState({}, "", "/pricing");
				return;
			}
			// Retry plan subscription now that billing key is registered
			const planResult = await paymentApi.requestPlanPayment({
				plan: pendingPlan as "BASIC" | "PRO" | "ENTERPRISE",
			});
			if (cancelled) return;
			setPlanLoading(false);
			if ("success" in planResult && planResult.success) {
				setPlanMessage(t("pricing.planSubscribeSuccess"));
			} else if ("error" in planResult) {
				setPlanMessage(planResult.error);
			}
			window.history.replaceState({}, "", "/pricing");
		})();
		return () => {
			cancelled = true;
		};
	}, [isLoggedIn]);

	const handleSelect = async (planKey: string) => {
		if (!isLoggedIn || !user?.id) {
			navigate({ to: "/login" });
			return;
		}
		if (user?.plan === planKey) return;
		if (planKey === "ENTERPRISE") {
			window.location.href = `mailto:${CONTACT_EMAIL}`;
			return;
		}
		setPlanLoading(true);
		setPlanMessage(null);
		try {
			const result = await paymentApi.requestPlanPayment({
				plan: planKey as "BASIC" | "PRO" | "ENTERPRISE",
			});
			if ("success" in result && result.success) {
				setPlanMessage(t("pricing.planSubscribeSuccess"));
				setPlanLoading(false);
				return;
			}
			if ("needBillingAuth" in result && result.needBillingAuth) {
				// No billing key — open Toss card registration with plan info in redirect URL
				const tossPayments = await loadTossPayments(TOSS_CLIENT_KEY);
				const payment = tossPayments.payment({ customerKey: result.customerKey });
				const origin = window.location.origin;
				await payment.requestBillingAuth({
					method: "CARD",
					successUrl: `${origin}/pricing?billing=success&plan=${planKey}`,
					failUrl: `${origin}/pricing?billing=fail`,
					customerEmail: user?.email ?? undefined,
					customerName: user?.name ?? undefined,
				});
				return;
			}
			if ("error" in result) {
				setPlanMessage(result.error);
			}
		} catch {
			setPlanMessage(t("pricing.planSubscribeFailed"));
		} finally {
			setPlanLoading(false);
		}
	};

	return (
		<VStack className="w-full bg-bg-weak pt-[100px] md:pt-[160px]">
			<NavBar />
			<VStack className="gap-[6px] px-[20px]">
				<span className="text-[1.5rem] md:text-[2.25rem] text-ct-strong font-semibold">{t("pricing.title")}</span>
				<span className="text-lg text-ct-regular font-medium">{t("pricing.description")}</span>
			</VStack>
			<HStack className="w-full max-w-[1200px] py-[60px] md:py-[100px] px-[20px] md:px-0 gap-[20px] flex-col md:flex-row">
				{plans.map((plan) => (
					<PricingCard
						key={plan.key}
						title={t(plan.titleKey)}
						description={t(plan.descKey)}
						price={plan.key === "ENTERPRISE" ? t("pricing.customPrice") : `₩${plan.price.toLocaleString()}`}
						llm={plan.llm}
						workflowAmount={plan.maxWorkflows}
						gyulAmount={plan.monthlyGyul}
						dashboard={t(plan.dashboardKey)}
						enterprise={t(plan.enterpriseKey)}
						isSelected={isLoggedIn && user?.plan === plan.key}
						buttonLabel={plan.key === "ENTERPRISE" ? t("pricing.contact") : undefined}
						onBtnTap={() => !planLoading && handleSelect(plan.key)}
					/>
				))}
			</HStack>
			{planMessage && (
				<VStack className="w-full max-w-[1200px] pb-[60px] items-center">
					<span className="text-sm text-ct-regular">{planMessage}</span>
				</VStack>
			)}
			<Footer />
		</VStack>
	);
};

export const pricingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pricing",
	component: Desktop,
});
